import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  getOrders,
  getOrder,
  updateOrderStatus,
  getOrderHistory,
  purgeCompletedOrders,
} from '../api/orders.api';
import type {
  OrdersResponse,
  OrdersQueryParams,
  OrderDetail,
  UpdateOrderStatusRequest,
  UpdateOrderStatusResponse,
  OrderStatus,
  OrderHistoryResponse,
  OrderHistoryQueryParams,
  PurgeCompletedOrdersResponse,
} from '../types/orders.types';
import type { ApiError } from '@/shared/lib/problem-details';
import { useAuthStore } from '@/features/auth/stores/auth-store';

/* ==========================================================================
   Query Keys — scoped by tenant
   ========================================================================== */

export const ordersKeys = {
  root: (tenantId: string) => ['orders', tenantId] as const,
  list: (tenantId: string, params: Record<string, unknown>) =>
    [...ordersKeys.root(tenantId), 'list', params] as const,
  detail: (tenantId: string, id: string) =>
    [...ordersKeys.root(tenantId), 'detail', id] as const,
  history: (tenantId: string, params: Record<string, unknown>) =>
    [...ordersKeys.root(tenantId), 'history', params] as const,
  /**
   * As solicitações de cancelamento do cliente (change
   * orders-customer-cancellation), fora da raiz: o chip e o alerta global a
   * leem em toda aba, e debaixo da raiz cada evento de pedido refaria a
   * consulta em toda loja, inclusive nas sem o Atendente IA. Relê nos dois
   * eventos da solicitação, na reconexão e no sucesso ou erro das decisões e
   * da mudança de status (cancelar, despachar e concluir encerram a
   * solicitação aguardando), por `invalidateCancellationRequests`.
   */
  cancellationRequests: (tenantId: string) => ['orders-cancellation-requests', tenantId] as const,
  /**
   * As alterações que esperam a loja (change orders-customer-amendments), fora
   * da raiz pelo mesmo motivo da lista de cancelamento: relê nos três eventos
   * da alteração, na reconexão e no sucesso ou erro das decisões e da mudança
   * de status (despachar e concluir recusam a pendente), por
   * `invalidateAmendmentRequests`.
   */
  amendmentRequests: (tenantId: string) => ['orders-amendment-requests', tenantId] as const,
  /**
   * A política do cliente ("Cancelar e alterar pelo WhatsApp"), fora da raiz:
   * um evento de pedido não relê a configuração com o formulário aberto.
   */
  customerPolicy: (tenantId: string) => ['orders-customer-policy', tenantId] as const,
};

/** Relê a lista de solicitações de cancelamento da loja. */
export function invalidateCancellationRequests(queryClient: QueryClient, tenantId: string) {
  void queryClient.invalidateQueries({ queryKey: ordersKeys.cancellationRequests(tenantId) });
}

/** Relê a lista de alterações que esperam a loja. */
export function invalidateAmendmentRequests(queryClient: QueryClient, tenantId: string) {
  void queryClient.invalidateQueries({ queryKey: ordersKeys.amendmentRequests(tenantId) });
}

function ordersTenantId(): string {
  return useAuthStore.getState().selectedTenant?.id ?? '';
}

/* ==========================================================================
   Queries
   ========================================================================== */

export function useOrders(params: OrdersQueryParams = {}) {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  return useQuery<OrdersResponse, ApiError>({
    queryKey: ordersKeys.list(tenantId, params as Record<string, unknown>),
    queryFn: () => getOrders(params),
    enabled: !!tenantId,
    refetchInterval: 30_000,
  });
}

export function useOrder(id: string | null) {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  return useQuery<OrderDetail, ApiError>({
    queryKey: ordersKeys.detail(tenantId, id ?? ''),
    queryFn: () => getOrder(id!),
    enabled: !!tenantId && !!id,
  });
}

export function useOrderHistory(params: OrderHistoryQueryParams = {}) {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  return useQuery<OrderHistoryResponse, ApiError>({
    queryKey: ordersKeys.history(tenantId, params as Record<string, unknown>),
    queryFn: () => getOrderHistory(params),
    enabled: !!tenantId,
  });
}

/* ==========================================================================
   Mutations
   ========================================================================== */

export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();

  const invalidateOrder = (id: string) => {
    const tid = ordersTenantId();
    if (tid) {
      queryClient.invalidateQueries({ queryKey: ordersKeys.detail(tid, id) });
      queryClient.invalidateQueries({ queryKey: ordersKeys.root(tid) });
      invalidateCancellationRequests(queryClient, tid);
      invalidateAmendmentRequests(queryClient, tid);
    }
  };

  return useMutation<
    UpdateOrderStatusResponse,
    ApiError,
    { id: string; data: UpdateOrderStatusRequest }
  >({
    mutationFn: ({ id, data }) => updateOrderStatus(id, data),
    onSuccess: (_result, { id }) => invalidateOrder(id),
    // No erro também: 409 e 422 querem dizer que a tela está atrás do
    // servidor, e a lista e o detalhe precisam do status de agora. Fica no
    // hook, e não no `mutate` do botão, porque o callback do `mutate` só roda
    // com o componente montado — e a atualização por tempo real pode ter
    // tirado o botão da tela antes de a resposta chegar.
    onError: (_error, { id }) => invalidateOrder(id),
  });
}

export function usePurgeCompletedOrders() {
  const queryClient = useQueryClient();

  return useMutation<PurgeCompletedOrdersResponse, ApiError, void>({
    mutationFn: () => purgeCompletedOrders(),
    onSuccess: () => {
      const tid = ordersTenantId();
      if (tid) {
        queryClient.invalidateQueries({ queryKey: ordersKeys.root(tid) });
      }
    },
  });
}

/* ==========================================================================
   Helpers for SignalR integration
   ========================================================================== */

/**
 * Invalidate order queries for the **current** selected tenant.
 */
export function useInvalidateOrders() {
  const queryClient = useQueryClient();

  return useMemo(
    () => ({
      invalidateAll: () => {
        const tid = useAuthStore.getState().selectedTenant?.id ?? '';
        if (tid) {
          queryClient.invalidateQueries({ queryKey: ordersKeys.root(tid) });
        }
      },
      invalidateList: (status?: OrderStatus) => {
        const tid = useAuthStore.getState().selectedTenant?.id ?? '';
        if (!tid) {
          return;
        }
        if (status) {
          queryClient.invalidateQueries({
            queryKey: ordersKeys.list(tid, { status } as Record<string, unknown>),
          });
        } else {
          queryClient.invalidateQueries({ queryKey: ordersKeys.root(tid) });
        }
      },
      invalidateDetail: (id: string) => {
        const tid = useAuthStore.getState().selectedTenant?.id ?? '';
        if (tid) {
          queryClient.invalidateQueries({ queryKey: ordersKeys.detail(tid, id) });
        }
      },
    }),
    [queryClient],
  );
}
